import { computed } from 'vue';
import { toValue } from '../../vueuse/toValue';
import { useDataStore } from '../../../composables/useVuexStore';
import { SIZES, SELECTED_SIZE } from '../../../store_modules/traitsStore/types';

function connectToStore() {
  const selectedStore = useDataStore(SELECTED_SIZE);

  /** @type {{
  * set: (id: string, data: ParsedSizes) => void,
  * get: (id: string) => ParsedSizes | null,
  * has: (id: string) => boolean,
  * }}
  */
  const dataStore = useDataStore(SIZES);

  return { selectedStore, dataStore };
}

/**
 * Initializes the data in local "store".
 * This should only need to be called once.
 * This should not need to be called outside of the sizes file.
 * @param {{ id: string, data: ParsedSizes}} obj
 */
export function useInitSize({ id, data }) {
  const { selectedStore, dataStore } = connectToStore();

  /**
  * @param {ProductId} productId
  * @param {ParsedSizes} sizeData
  */
  function setDefaultSize(productId, sizeData) {
    const sizes = sizeData.sizeArray;
    if (sizes.length === 1) {
      selectedStore.set(productId, sizes[0]);
    } else {
      selectedStore.set(productId, sizeData.sizeMap[sizeData.selectedSize] || null);
    }
  }

  if (!selectedStore.has(id)) {
    setDefaultSize(id, data);
  }

  if (!dataStore.has(id)) {
    dataStore.set(id, data);
  }
}

/**
 *
 * @param {import('@vueuse/core').MaybeRef<string>} id
 */
export function useSize(id) {
  const { selectedStore, dataStore } = connectToStore();

  const sizeData = computed(() => dataStore.get(toValue(id)));

  /**
   * @param {Size | SizeId | string | null} size
   */
  function setSize(size) {
    if (typeof size === 'number' || typeof size === 'string') {
      selectedStore.set(toValue(id), sizeData.value?.sizeMap[size] || null);
    } else {
      selectedStore.set(toValue(id), size);
    }
  }

  const selectedSize = computed({
    get() {
      return selectedStore.get(toValue(id));
    },
    set(newValue) {
      setSize(newValue);
    },
  });

  const selectedSizeId = computed({
    get() {
      return selectedSize.value?.id ?? null;
    },
    set(newValue) {
      setSize(newValue);
    },
  });

  const sizes = computed(() => sizeData.value?.sizeArray || []);
  const label = computed(() => sizeData.value?.label || 'Size');

  return {
    label,
    sizes,
    selectedSize,
    selectedSizeId,
  };
}
